import { domainCarriesCompanyName, isAcademicDomain, isDisposableDomain, isFreemailDomain } from './email.js';
import { emailDomain, registrableDomains } from './keys.js';
import { cleanCompanyName } from './names.js';
import { companyToken } from './repair.js';
/**
 * Zoho CRM as this product sees it: three modules, records keyed by Zoho's own
 * ids, and companies that very often have no website on file.
 *
 * Nothing here talks to Zoho. These are the shapes the sync and the import agree
 * on, and the one piece of arithmetic that has to be the same wherever it runs:
 * working out which domain a CRM Account is really at.
 */
export const ZOHO_MODULES = ['Accounts', 'Contacts', 'Leads'];
/** Zoho's data centres. An org lives in exactly one, and every URL follows it. */
export const ZOHO_DCS = ['in', 'com', 'eu', 'au', 'jp', 'ca', 'sa'];
export const ZOHO_PUSH_MODES = ['auto', 'manual'];
/** The key a Zoho record is stored under in Quarry: `<Module>#<id>`. */
export function zohoKey(module, id) {
    return `${module}#${id}`;
}
export function parseZohoKey(key) {
    const m = key ? /^(Accounts|Contacts|Leads)#(\d{5,25})$/.exec(key) : null;
    return m ? { module: m[1], id: m[2] } : null;
}
/**
 * What a CRM Account is grouped under while nothing better is known.
 *
 * A registrable domain when the website says so; otherwise the company's
 * distinctive name token, marked so it can never be mistaken for a domain. Null
 * for a name too short or too common to stand alone ("Batch"), which then files
 * its people by name only.
 */
export function companyKeyFor(website, name) {
    const domain = registrableDomains(website)[0];
    if (domain)
        return domain;
    const token = name ? companyToken(cleanCompanyName(name).clean) : null;
    return token ? `name:${token}` : null;
}
export function isNameCompanyKey(key) {
    return typeof key === 'string' && key.startsWith('name:');
}
/**
 * The registrable domain of an email address, when it could belong to a company.
 * A mailbox at Gmail, a university or a throwaway service says who the person
 * is, and nothing about where they work.
 */
export function companyEmailDomain(email) {
    const domain = emailDomain(email);
    if (!domain)
        return null;
    if (isFreemailDomain(domain) || isAcademicDomain(domain) || isDisposableDomain(domain))
        return null;
    return domain;
}
function rankVotes(votes, accountName) {
    return Object.entries(votes)
        .filter(([domain, count]) => domain && count > 0)
        .map(([domain, count]) => ({ domain, count, carriesName: domainCarriesCompanyName(domain, accountName) }))
        .sort((a, b) => b.count - a.count ||
        Number(b.carriesName) - Number(a.carriesName) ||
        a.domain.length - b.domain.length ||
        a.domain.localeCompare(b.domain));
}
/**
 * Which domain a CRM Account is at, when its website field is empty.
 *
 * Most of the Accounts in the CRM have no website, and every one of their
 * Contacts has an email. Five people at `@procloz.com` filed under "PROCLOZ" is
 * as good a website as the field would have been. One person is not: a lone
 * address might be a consultant, a customer or a typo, so a single witness is
 * believed only when the domain carries the company's own name.
 *
 * The website wins outright when present. After that, the Account's own
 * Contacts, then Leads that name the same company, by website then by email.
 * Whatever is not trusted is handed back as candidates rather than dropped.
 */
export function deriveAccountDomain(input) {
    const seen = new Set();
    const note = (s) => s;
    const fromWebsite = registrableDomains(input.website).filter((d) => !isFreemailDomain(d));
    if (fromWebsite.length > 0) {
        return {
            domain: fromWebsite[0],
            aliases: fromWebsite.slice(1),
            basis: 'website',
            candidates: [],
            note: note('From the website on the Account'),
        };
    }
    const tryVotes = (votes, basis, what) => {
        const ranked = rankVotes(votes, input.accountName);
        for (const v of ranked)
            seen.add(v.domain);
        const top = ranked[0];
        if (!top)
            return null;
        const trusted = top.count >= 2 || top.carriesName;
        if (!trusted)
            return null;
        const aliases = ranked
            .slice(1)
            .filter((v) => v.count >= 2)
            .map((v) => v.domain);
        const witnesses = top.count === 1 ? 'one address carrying the company name' : `${top.count} ${what}`;
        return {
            domain: top.domain,
            aliases,
            basis,
            candidates: [],
            note: note(`From ${witnesses} at ${top.domain}`),
        };
    };
    const byContacts = tryVotes(input.contactEmailDomains, 'contact_emails', 'contact addresses');
    if (byContacts)
        return byContacts;
    const leadSiteVotes = {};
    for (const site of input.leadWebsiteDomains) {
        for (const d of registrableDomains(site)) {
            if (isFreemailDomain(d))
                continue;
            leadSiteVotes[d] = (leadSiteVotes[d] ?? 0) + 1;
        }
    }
    const byLeadSites = tryVotes(leadSiteVotes, 'lead_websites', 'lead websites');
    if (byLeadSites)
        return byLeadSites;
    const byLeadEmails = tryVotes(input.leadEmailDomains, 'lead_emails', 'lead addresses');
    if (byLeadEmails)
        return byLeadEmails;
    const candidates = [...seen];
    return {
        domain: null,
        aliases: [],
        basis: null,
        candidates,
        note: note(candidates.length > 0
            ? `No domain trusted: ${candidates.join(', ')} each seen once without the company name`
            : 'No website on the Account and no company address among its people'),
    };
}
// ---------- Suppressions ------------------------------------------------------
/**
 * Why a person is kept out of everything: bought for, pushed to the CRM, or
 * exported. `erasure` is the one that must survive anything — a re-import, a
 * re-run, a rollback — because it is the record that somebody asked to be
 * forgotten. `company` keeps a whole domain out: a customer, a live deal.
 */
export const SUPPRESSION_KINDS = ['erasure', 'opt_out', 'do_not_contact', 'company'];
export const SUPPRESSION_SOURCES = ['manual', 'crm', 'outcome', 'import'];
export function contactSuppressionKey(contactKey) {
    return `contact#${contactKey}`;
}
export function emailSuppressionKey(email) {
    return `email#${email.trim().toLowerCase()}`;
}
export function zohoSuppressionKey(module, id) {
    return `zoho#${module}#${id}`;
}
export function domainSuppressionKey(domain) {
    return `domain#${domain.trim().toLowerCase()}`;
}
// ---------- The one-time import ----------------------------------------------
export const ZOHO_IMPORT_PHASES = [
    'accounts',
    'contacts',
    'leads',
    'resolve',
    'resolve_by_name',
    'finish',
];
export function emptyImportReport() {
    const people = () => ({
        total: 0,
        withEmail: 0,
        withMobile: 0,
        withLinkedin: 0,
        matchedQuarryContact: 0,
        duplicateEmailInCrm: 0,
        suppressed: 0,
        optOut: 0,
        unchanged: 0,
        created: 0,
        updated: 0,
        converted: 0,
        spam: 0,
    });
    return {
        accounts: {
            total: 0,
            matchedExistingCompany: 0,
            newCompany: 0,
            noDomain: 0,
            domainFrom: {},
            resolveByNameNeeded: 0,
            resolveByNameCredits: 0,
            unchanged: 0,
            created: 0,
            updated: 0,
        },
        contacts: people(),
        leads: people(),
        samples: { duplicateEmails: [], noDomainAccounts: [], conflicts: 0 },
    };
}
/** Zoho's documented daily allowance for an Enterprise org with this many licences. */
export const ZOHO_DAILY_CREDITS = 75_000;
//# sourceMappingURL=crm.js.map